import { useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import {
  FUND_ALLOCATIONS_BY_MD_DEFAULT_ERROR_MESSAGE,
  FUND_ALLOCATIONS_BY_MD_QUERY_KEY,
  SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/funds';
import { FundService } from 'services';
import { throwError } from 'utilities';
import {
  GetFundAllocationsByMDParams,
  GetFundAllocationsByMDResponse,
  GetFundAllocationsByMDReturn,
  UseGetFundAllocationsByMDParams,
} from './useGetFundAllocationsByMD.types';

const getFundAllocationsByMD = async (
  params: GetFundAllocationsByMDParams
): Promise<GetFundAllocationsByMDResponse | null> => {
  const { fundId, measurementDateId } = params;

  if (isNil(fundId) || isNil(measurementDateId)) {
    return null;
  }

  const fundService = new FundService();

  try {
    const fundAllocations = (await fundService.getFundAllocationsByMd(
      toString(fundId),
      toString(measurementDateId)
    )) as GetFundAllocationsByMDResponse;

    if (!fundAllocations) {
      return throwError(FUND_ALLOCATIONS_BY_MD_DEFAULT_ERROR_MESSAGE);
    }

    return fundAllocations;
  } catch (error) {
    return throwError(FUND_ALLOCATIONS_BY_MD_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFundAllocationsByMD = (params: UseGetFundAllocationsByMDParams): GetFundAllocationsByMDReturn => {
  const { fund, measurementDate, shouldQueryAutomatically = true } = params;

  const fundId = fund?.id;
  const measurementDateId = measurementDate?.id;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
    GetFundAllocationsByMDReturn['data'],
    Error
  >({
    enabled: !isNil(fundId) && !isNil(measurementDateId) && shouldQueryAutomatically,
    queryFn: () => getFundAllocationsByMD({ fundId, measurementDateId }),
    queryKey: [FUND_ALLOCATIONS_BY_MD_QUERY_KEY, fundId, measurementDateId],
    refetchOnWindowFocus: SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFundAllocationsByMD;
